/**
 * ControlledInventoryPage - Domain-agnostic controlled inventory page (DRR-232)
 *
 * Generic page for any industry's controlled/regulated item counting:
 * - Healthcare: Controlled substance (narcotic) counts
 * - Finance: Restricted asset verification
 * - Legal: Trust fund account reconciliation
 * - Construction: Hazmat inventory tracking
 *
 * The page uses generic queries; domain-specific rendering is handled
 * by RendererRegistry, which resolves renderers from subgraph packages.
 *
 * Access control:
 *   - RequireScope("inventory:count") on backend
 *   - Typically requires two-person verification
 */

import { useState, Suspense } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Alert,
  Button,
  Group,
  Badge,
  Card,
  Divider,
  LoadingOverlay,
  Table,
  ThemeIcon,
  Skeleton,
  Progress,
  RingProgress,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconRefresh,
  IconAlertCircle,
  IconShieldCheck,
  IconScale,
} from '@tabler/icons-react';
// eslint-disable-next-line no-restricted-imports -- Data layer hook requires direct Apollo access
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

import { useAuth } from '@/auth';
import { useResolvedTerminology } from '@/hooks/useResolvedTerminology';
import { useRenderer } from '@/components/RendererRegistry';

// =============================================================================
// GraphQL - Uses generic queries from Track B (DRR-232)
// =============================================================================

const CONTROLLED_ITEM_COUNT = gql`
  query ControlledItemCount($constraintId: ID!) {
    controlledItemCount(constraintId: $constraintId) {
      constraintId
      itemType
      expectedCount
      actualCount
      variance
      lastVerifiedAt
      verifiedBy
      nextVerificationDue
    }
  }
`;

const CONTROLLED_ITEMS_NEEDING_VERIFICATION = gql`
  query ControlledItemsNeedingVerification {
    controlledItemsNeedingVerification {
      constraintId
      itemType
      itemName
      location
      lastVerifiedAt
      verificationStatus
      urgencyLevel
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

interface ControlledItemCount {
  constraintId: string;
  itemType: string;
  expectedCount: number;
  actualCount: number;
  variance: number;
  lastVerifiedAt: string | null;
  verifiedBy: string | null;
  nextVerificationDue: string | null;
}

interface VerificationItem {
  constraintId: string;
  itemType: string;
  itemName: string;
  location: string;
  lastVerifiedAt: string | null;
  verificationStatus: string;
  urgencyLevel: string;
}

// Status/Urgency constants (matches backend enums)
// Defined locally until codegen regenerates from updated schema
// Using const objects to satisfy DRR-074 (no hardcoded status strings)
const UrgencyLevel = {
  Critical: 'CRITICAL',
  High: 'HIGH',
  Medium: 'MEDIUM',
  Low: 'LOW',
} as const;

const VerificationStatus = {
  Pending: 'PENDING',
  Overdue: 'OVERDUE',
  Verified: 'VERIFIED',
} as const;

// =============================================================================
// Variance Badge
// =============================================================================

function VarianceBadge({ variance }: { variance: number }) {
  if (variance === 0) {
    return (
      <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
        Balanced
      </Badge>
    );
  }

  const color = Math.abs(variance) > 0 ? 'red' : 'orange';
  const sign = variance > 0 ? '+' : '';

  return (
    <Badge color={color} variant="filled">
      {sign}
      {variance}
    </Badge>
  );
}

// =============================================================================
// Urgency Badge
// =============================================================================

function UrgencyBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    CRITICAL: 'red',
    HIGH: 'orange',
    MEDIUM: 'yellow',
    LOW: 'gray',
  };

  return (
    <Badge color={colors[level] ?? 'gray'} variant="dot" size="sm">
      {level.toLowerCase()}
    </Badge>
  );
}

// =============================================================================
// Count Card
// =============================================================================

function CountCard({
  item,
  onSelect,
  selected,
}: {
  item: ControlledItemCount;
  onSelect: () => void;
  selected: boolean;
}) {
  const isBalanced = item.variance === 0;
  const progressColor = isBalanced ? 'green' : 'red';
  const progressValue =
    item.expectedCount > 0
      ? Math.min(100, (item.actualCount / item.expectedCount) * 100)
      : 0;

  return (
    <Card
      shadow={selected ? 'md' : 'sm'}
      padding="md"
      radius="md"
      withBorder
      style={{
        cursor: 'pointer',
        borderColor: selected
          ? 'var(--mantine-color-blue-5)'
          : !isBalanced
          ? 'var(--mantine-color-red-3)'
          : undefined,
        backgroundColor: selected
          ? 'var(--mantine-color-blue-0)'
          : !isBalanced
          ? 'var(--mantine-color-red-0)'
          : undefined,
      }}
      onClick={onSelect}
    >
      <Group justify="space-between" mb="sm">
        <Text fw={600}>{item.itemType}</Text>
        <VarianceBadge variance={item.variance} />
      </Group>

      <Group justify="space-between" mb="xs">
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Expected
          </Text>
          <Text fw={500}>{item.expectedCount}</Text>
        </Stack>
        <Stack gap={2} ta="right">
          <Text size="xs" c="dimmed">
            Actual
          </Text>
          <Text fw={500}>{item.actualCount}</Text>
        </Stack>
      </Group>

      <Progress value={progressValue} color={progressColor} size="sm" mb="sm" />

      <Divider my="xs" />

      <Group justify="space-between">
        <Group gap="xs">
          <IconClock size={12} />
          <Text size="xs" c="dimmed">
            {item.lastVerifiedAt
              ? `Verified ${new Date(item.lastVerifiedAt).toLocaleDateString()}`
              : 'Never verified'}
          </Text>
        </Group>
        {item.nextVerificationDue && (
          <Text size="xs" c="dimmed">
            Due: {new Date(item.nextVerificationDue).toLocaleDateString()}
          </Text>
        )}
      </Group>
    </Card>
  );
}

// =============================================================================
// Items Needing Verification Table
// =============================================================================

function VerificationTable({
  items,
  onSelect,
}: {
  items: VerificationItem[];
  onSelect: (constraintId: string) => void;
}) {
  if (items.length === 0) return null;

  return (
    <Alert
      color="orange"
      title={`${items.length} item${items.length !== 1 ? 's' : ''} need verification`}
      icon={<IconAlertCircle size={18} />}
    >
      <Table mt="md" striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Item</Table.Th>
            <Table.Th>Location</Table.Th>
            <Table.Th>Last Verified</Table.Th>
            <Table.Th>Urgency</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((item) => (
            <Table.Tr
              key={item.constraintId}
              onClick={() => onSelect(item.constraintId)}
              style={{ cursor: 'pointer' }}
            >
              <Table.Td>
                <Text size="sm" fw={500}>
                  {item.itemName}
                </Text>
                <Text size="xs" c="dimmed">
                  {item.itemType}
                </Text>
              </Table.Td>
              <Table.Td>{item.location}</Table.Td>
              <Table.Td>
                {item.lastVerifiedAt
                  ? new Date(item.lastVerifiedAt).toLocaleDateString()
                  : 'Never'}
              </Table.Td>
              <Table.Td>
                <UrgencyBadge level={item.urgencyLevel} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Alert>
  );
}

// =============================================================================
// Detail Panel with Dynamic Renderer
// =============================================================================

function DetailPanel({
  item,
  rendererName,
}: {
  item: ControlledItemCount;
  rendererName?: string;
}) {
  const DomainRenderer = useRenderer(rendererName);

  // If we have a domain-specific renderer, use it
  if (DomainRenderer) {
    return (
      <Suspense fallback={<Skeleton height={300} />}>
        <DomainRenderer entity={item as unknown as Record<string, unknown>} />
      </Suspense>
    );
  }

  // Generic fallback renderer
  const isBalanced = item.variance === 0;

  return (
    <Stack gap="md">
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Text fw={600} size="lg">
              {item.itemType}
            </Text>
            <Text size="sm" c="dimmed">
              Constraint ID: {item.constraintId}
            </Text>
          </div>
          <RingProgress
            size={80}
            thickness={8}
            sections={[
              {
                value:
                  item.expectedCount > 0
                    ? (item.actualCount / item.expectedCount) * 100
                    : 0,
                color: isBalanced ? 'green' : 'red',
              },
            ]}
            label={
              <ThemeIcon
                size="lg"
                radius="xl"
                color={isBalanced ? 'green' : 'red'}
                variant="light"
              >
                {isBalanced ? <IconCheck size={20} /> : <IconAlertTriangle size={20} />}
              </ThemeIcon>
            }
          />
        </Group>

        <Divider my="md" />

        <Stack gap="md">
          <Group grow>
            <Paper p="sm" withBorder bg="gray.0">
              <Text size="xs" c="dimmed">
                Expected Count
              </Text>
              <Text size="xl" fw={700}>
                {item.expectedCount}
              </Text>
            </Paper>
            <Paper p="sm" withBorder bg="gray.0">
              <Text size="xs" c="dimmed">
                Actual Count
              </Text>
              <Text size="xl" fw={700}>
                {item.actualCount}
              </Text>
            </Paper>
            <Paper
              p="sm"
              withBorder
              bg={isBalanced ? 'green.0' : 'red.0'}
            >
              <Text size="xs" c="dimmed">
                Variance
              </Text>
              <Text size="xl" fw={700} c={isBalanced ? 'green' : 'red'}>
                {item.variance > 0 ? '+' : ''}
                {item.variance}
              </Text>
            </Paper>
          </Group>

          <Paper p="sm" withBorder>
            <Group justify="space-between">
              <div>
                <Text size="xs" c="dimmed">
                  Last Verified
                </Text>
                <Text size="sm">
                  {item.lastVerifiedAt
                    ? new Date(item.lastVerifiedAt).toLocaleString()
                    : 'Never verified'}
                </Text>
              </div>
              {item.verifiedBy && (
                <div>
                  <Text size="xs" c="dimmed">
                    Verified By
                  </Text>
                  <Text size="sm">{item.verifiedBy}</Text>
                </div>
              )}
            </Group>
          </Paper>

          {item.nextVerificationDue && (
            <Alert
              color="blue"
              title="Next Verification Due"
              icon={<IconClock size={18} />}
            >
              {new Date(item.nextVerificationDue).toLocaleString()}
            </Alert>
          )}

          {!isBalanced && (
            <Alert
              color="red"
              title="Count Discrepancy"
              icon={<IconAlertTriangle size={18} />}
            >
              <Text size="sm">
                There is a variance of {Math.abs(item.variance)} unit
                {Math.abs(item.variance) !== 1 ? 's' : ''}.
                {item.variance < 0
                  ? ' Actual count is less than expected.'
                  : ' Actual count exceeds expected.'}
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                Please verify count and investigate discrepancy.
              </Text>
            </Alert>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

// =============================================================================
// Main Page
// =============================================================================

export function ControlledInventoryPage() {
  const { user, tenant } = useAuth();
  const { getLabel } = useResolvedTerminology();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Controlled item label from terminology
  const itemLabel = getLabel('controlled_item') || 'Controlled Item';

  // Fetch items needing verification
  const { data: verificationData, loading: verificationLoading } = useQuery<{
    controlledItemsNeedingVerification: VerificationItem[];
  }>(CONTROLLED_ITEMS_NEEDING_VERIFICATION, {
    skip: !user,
    pollInterval: 60000,
  });

  // Fetch count for selected item
  const { data: countData, loading: countLoading } = useQuery<{
    controlledItemCount: ControlledItemCount;
  }>(CONTROLLED_ITEM_COUNT, {
    variables: { constraintId: selectedId ?? '' },
    skip: !selectedId,
  });

  // Get renderer name from tenant metadata
  const rendererName = tenant?.features?.['controlledInventoryRenderer'] as
    | string
    | undefined;

  const verificationItems: VerificationItem[] =
    verificationData?.controlledItemsNeedingVerification ?? [];
  const selectedCount: ControlledItemCount | null =
    countData?.controlledItemCount ?? null;

  // Summary stats
  const criticalCount = verificationItems.filter(
    (i) => i.urgencyLevel === UrgencyLevel.Critical
  ).length;
  const highCount = verificationItems.filter((i) => i.urgencyLevel === UrgencyLevel.High).length;

  // Access check
  if (!user) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Authentication Required" icon={<IconAlertTriangle />}>
          <Text>Please log in to access controlled inventory.</Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>{itemLabel} Inventory</Title>
            <Text c="dimmed" size="sm">
              Track and verify regulated items
            </Text>
          </div>
          <Group>
            {criticalCount > 0 && (
              <Badge color="red" size="lg">
                {criticalCount} Critical
              </Badge>
            )}
            {highCount > 0 && (
              <Badge color="orange" size="lg">
                {highCount} High Priority
              </Badge>
            )}
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              loading={verificationLoading}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Summary cards */}
        <Group grow>
          <Paper p="md" withBorder>
            <Group gap="md">
              <ThemeIcon size="xl" variant="light" color="blue">
                <IconScale size={24} />
              </ThemeIcon>
              <div>
                <Text size="sm" c="dimmed">
                  Total Items
                </Text>
                <Text size="xl" fw={700}>
                  {verificationItems.length}
                </Text>
              </div>
            </Group>
          </Paper>
          <Paper p="md" withBorder>
            <Group gap="md">
              <ThemeIcon size="xl" variant="light" color="green">
                <IconShieldCheck size={24} />
              </ThemeIcon>
              <div>
                <Text size="sm" c="dimmed">
                  Verified Today
                </Text>
                <Text size="xl" fw={700}>
                  {
                    verificationItems.filter(
                      (i) =>
                        i.lastVerifiedAt &&
                        new Date(i.lastVerifiedAt).toDateString() ===
                          new Date().toDateString()
                    ).length
                  }
                </Text>
              </div>
            </Group>
          </Paper>
          <Paper p="md" withBorder>
            <Group gap="md">
              <ThemeIcon size="xl" variant="light" color="orange">
                <IconAlertCircle size={24} />
              </ThemeIcon>
              <div>
                <Text size="sm" c="dimmed">
                  Pending Verification
                </Text>
                <Text size="xl" fw={700}>
                  {
                    verificationItems.filter(
                      (i) =>
                        i.verificationStatus === VerificationStatus.Pending ||
                        i.verificationStatus === VerificationStatus.Overdue
                    ).length
                  }
                </Text>
              </div>
            </Group>
          </Paper>
        </Group>

        {/* Items needing verification */}
        <VerificationTable
          items={verificationItems}
          onSelect={(id) => setSelectedId(id)}
        />

        {/* Empty state */}
        {!verificationLoading && verificationItems.length === 0 && (
          <Paper p="xl" withBorder ta="center">
            <ThemeIcon size="xl" variant="light" color="green" mx="auto" mb="md">
              <IconCheck size={24} />
            </ThemeIcon>
            <Title order={4}>All Items Verified</Title>
            <Text c="dimmed" size="sm">
              No controlled items currently need verification
            </Text>
          </Paper>
        )}

        {/* Detail panel for selected item */}
        {selectedId && (
          <Paper p="md" withBorder>
            <LoadingOverlay visible={countLoading} />
            {selectedCount ? (
              <DetailPanel item={selectedCount} rendererName={rendererName} />
            ) : (
              <Stack justify="center" h={200} align="center">
                <Text c="dimmed">Loading item details...</Text>
              </Stack>
            )}
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

export default ControlledInventoryPage;
